import React from 'react';
import {generateAIVideosText} from '../generateAIVideosText';
import {
  LANGUAGE_OPTIONS,
  LanguageKey,
  VIDEO_LENGTH_OPTIONS,
  VOICE_GENDER_MALE,
} from '../../../../data/models/AI/VideoGenerationOptions';
import {capitalizeFirstLetter} from '../../../../utils/parseStringArrayToInt';
import {numberWithCommas} from '../../../../utils/getFirstCapitals';
import {
  ASSESMENT_OPTIONS,
  BRANCHING_OPTIONS,
} from '../../GenerateMicrolesson/conf';
import {
  AiAssistantCommunicationOptions,
  AiAssistantResponseOptions,
} from '../../../../data/models/AI/AiAssisntantOptions';

interface Props {
  filename: string;
  excludeSlides?: string;
  muteDuration?: string;
  muteSlides?: string;
  excludePhrases?: string;
  duration: string;
  generateImages: boolean;
  generateVideo: boolean;
  imageRatio?: number;
  language: LanguageKey;
  dialect: string;
  voiceGender: string;
  estimatedCost: number;
  availableCredits: number;
  responseStyle: string;
  responseTone: string;
  imageStyle?: string;
  isEstimationLoading: boolean;
  exactNarration: boolean;
  generateteMicrolesson: boolean;
  microlessonTitle: string;
  includeBranching: boolean;
  includeGrading: boolean;
  passingScore?: number;
  addAiAssistant: boolean;
  assistantName: string;
  assistantDescription: string;
  assistantResponseStyle: string;
  assistantCommunicationTone: string;
  backgroundMusicType?: string;
}

const SummaryStep = React.memo(
  ({
    filename,
    excludePhrases,
    muteDuration,
    muteSlides,
    excludeSlides,
    duration,
    generateImages,
    generateVideo,
    imageRatio,
    language,
    dialect,
    voiceGender,
    estimatedCost,
    availableCredits,
    responseStyle,
    responseTone,
    imageStyle,
    isEstimationLoading,
    exactNarration,
    generateteMicrolesson,
    microlessonTitle,
    includeBranching,
    includeGrading,
    passingScore,
    addAiAssistant,
    assistantName,
    assistantDescription,
    assistantResponseStyle,
    assistantCommunicationTone,
    backgroundMusicType,
  }: Props) => {
    const getExceptions = () => {
      const exceptions = [];
      if (excludeSlides) {
        exceptions.push(generateAIVideosText.summaryExclude + excludeSlides);
      }
      if (muteSlides && muteDuration) {
        exceptions.push(
          generateAIVideosText.summaryMute +
            muteSlides +
            generateAIVideosText.summaryFor +
            muteDuration,
        );
      }
      if (excludePhrases) {
        exceptions.push(
          generateAIVideosText.summaryExcludePhrases + ': ' + excludePhrases,
        );
      }
      if (exceptions.length) {
        return exceptions.reduce((prev, curr) => (prev += ', ' + curr));
      }
      return '';
    };

    return (
      <div className="StepContainer SummaryStep">
        <h5 className="StepTitle">{generateAIVideosText.summary}</h5>
        <h3>{generateAIVideosText.summaryReady}</h3>
        <p>
          {generateteMicrolesson
            ? generateAIVideosText.summaryInfo_YesMicrolesson
            : generateAIVideosText.summaryInfo_NoMicrolesson}
        </p>
        <h3>{generateAIVideosText.summary}</h3>
        <ul>
          <li>
            {generateAIVideosText.summaryFile}
            {filename}
          </li>
          {getExceptions() !== '' && (
            <li>
              {generateAIVideosText.summaryExceptions}
              {getExceptions()}
            </li>
          )}
          <li>
            {generateAIVideosText.summaryVideoDuration}
            {VIDEO_LENGTH_OPTIONS.find(x => x.key === duration)?.value}
          </li>
          <li>
            {generateAIVideosText.summaryMedia}
            {generateImages && generateVideo
              ? `${imageRatio}%${
                  imageStyle ? ', ' + imageStyle : ''
                } and AI videos`
              : generateImages
              ? `${imageRatio}%${imageStyle ? ', ' + imageStyle : ''}`
              : generateVideo
              ? `${imageRatio}% AI Videos`
              : generateAIVideosText.summaryNoImages}
          </li>
          <li>
            {generateAIVideosText.summaryNarration}
            {LANGUAGE_OPTIONS.find(x => x.key === language)?.value}, {dialect},{' '}
            {voiceGender === VOICE_GENDER_MALE
              ? generateAIVideosText.summaryMale
              : generateAIVideosText.summaryFemale}
          </li>
          <li>
            {generateAIVideosText.summaryTone}
            {capitalizeFirstLetter(responseStyle)} &{' '}
            {capitalizeFirstLetter(responseTone)}
          </li>
          <li>
            {generateAIVideosText.slideNarrationTypeSummary}
            {exactNarration
              ? generateAIVideosText.narrationExact
              : generateAIVideosText.narrationEnhanced}
          </li>
          <li>
            {generateAIVideosText.generateMicrolessonStep_pageTitle}:&nbsp;
            {generateteMicrolesson ? 'Yes' : 'No'}
          </li>
          {generateteMicrolesson && (
            <div className="SummaryStep_MicrolessonDetails">
              <li>
                {generateAIVideosText.generateMicrolessonStep_titleField}:&nbsp;
                {microlessonTitle}
              </li>
              <li>
                {generateAIVideosText.generateMicrolessonStep_includeBranching}
                :&nbsp;
                {
                  BRANCHING_OPTIONS.find(
                    x => x.key === (includeBranching ? '1' : '0'),
                  )?.value
                }
              </li>
              <li>
                {generateAIVideosText.generateMicrolessonStep_includeGrading}
                :&nbsp;
                {
                  ASSESMENT_OPTIONS.find(
                    x => x.key === (includeGrading ? '1' : '0'),
                  )?.value
                }
              </li>
              {includeGrading && (
                <li>
                  {generateAIVideosText.generateMicrolessonStep_passingScore}
                  :&nbsp;
                  {passingScore !== undefined ? passingScore : ''}
                </li>
              )}
            </div>
          )}
          <li>
            {generateAIVideosText.aiAssistantStep_pageTitle}:&nbsp;
            {addAiAssistant ? 'Yes' : 'No'}
          </li>
          {addAiAssistant && (
            <div className="SummaryStep_MicrolessonDetails">
              <li>
                {generateAIVideosText.summaryAiAssistant_name}:&nbsp;
                {assistantName}
              </li>
              <li>
                {generateAIVideosText.summaryAiAssistant_description}:&nbsp;
                {assistantDescription}
              </li>
              <li>
                {generateAIVideosText.summaryAiAssistant_responseStyle}:&nbsp;
                {
                  AiAssistantResponseOptions.find(
                    x => x.key === assistantResponseStyle,
                  )?.value
                }
              </li>
              <li>
                {generateAIVideosText.summaryAiAssistant_communicationTone}
                :&nbsp;
                {
                  AiAssistantCommunicationOptions.find(
                    x => x.key === assistantCommunicationTone,
                  )?.value
                }
              </li>
            </div>
          )}
          <li>
            {generateAIVideosText.summaryBackgroundMusic}
            {backgroundMusicType ?? 'No'}
          </li>
          <li>
            <br />
          </li>
          <li>
            {generateAIVideosText.summaryAvailableCredits}
            <span
              className={`CreditsCost ${
                availableCredits < estimatedCost ? 'CreditError' : ''
              }`}>
              {isEstimationLoading
                ? ''
                : numberWithCommas(availableCredits.toString())}
            </span>
          </li>
          <li>
            {generateAIVideosText.summaryEstimatedCost}
            <span className="CreditsCost">
              {isEstimationLoading
                ? generateAIVideosText.calculating
                : numberWithCommas(estimatedCost.toString())}
            </span>
          </li>
        </ul>
      </div>
    );
  },
);

export default SummaryStep;
